import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Star, MessageSquare, Send } from "lucide-react";
import toast from "react-hot-toast";
import SummaryApi from "../common";

const UserProductReview = ({
  isOpen,
  onClose,
  reviewData,
  onReviewSubmitted,
}) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [reviewDescription, setReviewDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [checkingReview, setCheckingReview] = useState(false);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setRating(0);
      setHover(0);
      setReviewDescription("");
      setExistingReview(null);
      setCheckingReview(false);
      return;
    }

    const checkExistingReview = async () => {
      if (
        !reviewData?.product_id &&
        !reviewData?.order_id &&
        !reviewData?.user_pack_subscription_id
      )
        return;

      const sessionData = localStorage.getItem("user");
      let userId = null;
      try {
        const parsed = JSON.parse(sessionData);
        userId = parsed.user_id || parsed.id;
      } catch (err) {
        toast.error("Please login again");
        return;
      }

      setCheckingReview(true);

      try {
        let response;
        let payload;

        if (reviewData.type === "product") {
          payload = { user_id: userId, product_id: reviewData.product_id };
          response = await fetch(SummaryApi.getuserrating.url, {
            method: SummaryApi.getuserrating.method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
        } else if (
          reviewData.type === "sakhi" ||
          reviewData.type === "sponsorship"
        ) {
          payload = { user_id: userId, order_id: reviewData.order_id };
          response = await fetch(SummaryApi.getusersakhirating.url, {
            method: SummaryApi.getusersakhirating.method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
        } else if (reviewData.type === "subscription") {
          payload = {
            user_id: userId,
            user_pack_subscription_id: reviewData.user_pack_subscription_id,
          };
          response = await fetch(SummaryApi.getusersubscriptionrating.url, {
            method: SummaryApi.getusersubscriptionrating.method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
        } else {
          payload = {
            user_id: userId,
            ...(reviewData.product_id && { product_id: reviewData.product_id }),
            ...(reviewData.order_id && { order_id: reviewData.order_id }),
            ...(reviewData.user_pack_subscription_id && {
              user_pack_subscription_id: reviewData.user_pack_subscription_id,
            }),
          };
          response = await fetch(SummaryApi.getuserrating.url, {
            method: SummaryApi.getuserrating.method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
        }

        const data = await response.json();

        if (data.status && data.data) {
          const reviewData_response = Array.isArray(data.data)
            ? data.data[0]
            : data.data;
          setExistingReview(reviewData_response);
          setReviewDescription(reviewData_response.review_description || "");
        } else {
          setExistingReview(null);
          setReviewDescription("");
          setRating(0);
        }
      } catch (error) {
        console.error("Error checking review:", error);
        toast.error("Could not check previous review");
      } finally {
        setCheckingReview(false);
      }
    };

    checkExistingReview();
  }, [isOpen, reviewData]);

  const handleCloseModal = () => {
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reviewDescription.trim()) {
      return toast.error("Please write a short description of your experience");
    }

    setLoading(true);

    const today = new Date().toISOString().split("T")[0];

    const sessionData = localStorage.getItem("user");
    let userId = null;
    try {
      const parsed = JSON.parse(sessionData);
      userId = parsed.user_id || parsed.id;
    } catch (err) {
      toast.error("User session not found. Please login again.");
      setLoading(false);
      return;
    }

    try {
      let response;
      let payload;

      if (existingReview) {
        // UPDATE
        payload = {
          review_id: existingReview.review_id || existingReview.id,
          user_id: userId,
          review_description: reviewDescription,
          review_date: today,
          is_active: 1,
          product_id: 0,
          order_id: 0,
          user_pack_subscription_id: 0,
        };

        if (reviewData.type === "product") {
          payload.product_id = reviewData.product_id || 0;
        } else if (
          reviewData.type === "sakhi" ||
          reviewData.type === "sponsorship"
        ) {
          payload.order_id = reviewData.order_id || 0;
        } else if (reviewData.type === "subscription") {
          payload.user_pack_subscription_id =
            reviewData.user_pack_subscription_id || 0;
        }

        response = await fetch(SummaryApi.review_update.url, {
          method: SummaryApi.review_update.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        // CREATE
        payload = {
          user_id: userId,
          review_description: reviewDescription,
          review_date: today,
          is_active: 1,
          product_id: 0,
          order_id: 0,
          user_pack_subscription_id: 0,
        };

        if (reviewData.type === "product") {
          payload.product_id = reviewData.product_id || 0;
        } else if (
          reviewData.type === "sakhi" ||
          reviewData.type === "sponsorship"
        ) {
          payload.order_id = reviewData.order_id || 0;
        } else if (reviewData.type === "subscription") {
          payload.user_pack_subscription_id =
            reviewData.user_pack_subscription_id || 0;
        }

        response = await fetch(SummaryApi.review_create.url, {
          method: SummaryApi.review_create.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      const data = await response.json();

      if (data.status) {
        toast.success(
          existingReview
            ? "Your review has been updated successfully!"
            : "Thank you! Your review has been submitted.",
        );

        // ────────────────────────────────────────────────
        //   Reload page → same tab remains active
        // ────────────────────────────────────────────────
        window.location.reload();

        // The lines below will NOT execute after reload
        // onClose();
        // if (onReviewSubmitted) onReviewSubmitted();
      } else {
        toast.error(data.message || "Failed to submit review");
      }
    } catch (error) {
      console.error("Review Error:", error);
      toast.error("An error occurred while connecting to the server");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleCloseModal}
          className="absolute inset-0 bg-rose-950/40 backdrop-blur-sm"
        />
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden"
        >
          <div className="p-6">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-2xl font-black text-rose-950 uppercase tracking-tight">
                {existingReview
                  ? "Edit Your Review"
                  : reviewData?.type === "product"
                    ? "Write a Review"
                    : reviewData?.type === "sakhi"
                      ? "Share Your Impact"
                      : "Review Subscription"}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-rose-50 rounded-full transition-colors"
              >
                <X size={24} className="text-rose-400" />
              </button>
            </div>

            <div className="mb-5 bg-rose-50/50 p-4 rounded-2xl border border-rose-100">
              <p className="text-[10px] font-black text-rose-400 uppercase mb-1">
                {reviewData?.type === "product"
                  ? "Reviewing"
                  : reviewData?.type === "sakhi"
                    ? "Sharing Impact For"
                    : "Reviewing Subscription"}
              </p>
              <p className="text-sm font-bold text-rose-900 break-words">
                {reviewData?.name}
              </p>
            </div>

            {checkingReview ? (
              <div className="text-center py-8">
                <div className="animate-spin h-8 w-8 border-4 border-rose-200 border-t-rose-500 rounded-full mx-auto"></div>
                <p className="mt-4 text-rose-600 text-sm">
                  Checking your previous review...
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <p className="text-xs font-black text-rose-900/40 uppercase flex items-center gap-2">
                      <MessageSquare size={14} /> Review Description
                    </p>
                    {/* Character Counter */}
                    <span
                      className={`text-[10px] font-bold ${reviewDescription.length >= 300 ? "text-red-500" : "text-rose-400"}`}
                    >
                      {reviewDescription.length}/300
                    </span>
                  </div>
                  <textarea
                    value={reviewDescription}
                    onChange={(e) => setReviewDescription(e.target.value)}
                    maxLength={300} 
                    className="w-full bg-rose-50/30 border-2 border-rose-100 rounded-2xl p-4 text-rose-950 placeholder:text-rose-300 focus:outline-none focus:border-rose-400 transition-colors resize-none"
                    rows={4}
                    placeholder={
                      reviewData?.type === "product"
                        ? "Tell us about the quality..."
                        : reviewData?.type === "sakhi"
                          ? "Share how this impact..."
                          : "Share your experience..."
                    }
                  />
                  {reviewDescription.length >= 300 && (
                    <p className="text-[10px] text-red-500 font-medium">
                      Character limit reached.
                    </p>
                  )}
                </div>

                {existingReview && existingReview.review_date && (
                  <p className="text-xs text-rose-400">
                    Originally reviewed on{" "}
                    {new Date(existingReview.review_date).toLocaleDateString(
                      "en-GB",
                      {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      },
                    )}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-rose-500 text-white py-4 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-rose-600 shadow-lg shadow-rose-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      {existingReview ? "Update Review" : "Submit Review"}
                      <Send size={18} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default UserProductReview;
